import { useListingBatch } from "../hooks/useListingBatch";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays, Search as SearchIcon, X, MapPin, Loader2 } from "lucide-react";
import { fetchCalendarEvents, occurrenceVariant } from "../lib/events";
import { geocodePlace, fetchBoundary } from "../lib/eventApi";
import { distanceKm } from "../lib/geo";
import { searchEventsGeo, parseQuery, typePredicate } from "../lib/eventSearch";
import { countryName } from "../lib/countries";
import { getNavState, saveNavState } from "../lib/navState";
import { useCountry } from "../context/CountryContext";
import { occurrenceDays, localTodayKey } from "../lib/calendarDays";
import { EventCard } from "../components/EventCard";
import { AppMenu } from "../components/AppMenu";
import { CountrySelector } from "../components/CountrySelector";
import { AdSlot } from "../components/AdSlot";

// Area radius (km) around a searched place (city/town/postcode). ~50km covers a
// city/town and its surrounding meets without pulling in the whole country.
// States/provinces are NOT radius-matched (see lib/calendarSearch.js).
const AREA_RADIUS_KM = 50;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // UK week order
const WEEKDAY_LONG = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

// Local (calendar) day string for a Date, using its UTC parts (we always build the
// Date from a plain YYYY-MM-DD via T00:00:00Z, so UTC parts == the intended day).
const keyOf = (d) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate()
  ).padStart(2, "0")}`;

const todayKey = localTodayKey();

// Iterate each calendar day (inclusive) an occurrence spans. Occurrences are
// capped to a few days by the Add Event flow; a hard guard keeps this bounded.
function eachDay(startYmd, endYmd, cb) {
  if (!startYmd) return;
  const last = new Date((endYmd || startYmd) + "T00:00:00Z");
  const d = new Date(startYmd + "T00:00:00Z");
  let guard = 0;
  while (d <= last && guard < 40) {
    cb(keyOf(d));
    d.setUTCDate(d.getUTCDate() + 1);
    guard += 1;
  }
}

function formatSelected(ymd) {
  const d = new Date(ymd + "T00:00:00Z");
  return `${WEEKDAY_LONG[d.getUTCDay()]}, ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export default function CalendarPage() {
  const location = useLocation();
  const navKey = `calendar:${location.key}`;
  const savedNav = getNavState(navKey);
  const { country } = useCountry();
  const now = new Date();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(() => savedNav?.year ?? now.getFullYear());
  const [month, setMonth] = useState(() => savedNav?.month ?? now.getMonth()); // 0-based
  const [selectedKey, setSelectedKey] = useState(() => savedNav?.selectedKey ?? todayKey);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(now.getFullYear());
  // Within-country search (NOT the user's current location, NOT the country —
  // that is the global header selector): state, city, venue, name, category or
  // keyword. `applied` = { query, center } once submitted; center is a geocoded
  // { lat, lng } only for place-like queries (city/town/postcode), else null.
  const [locInput, setLocInput] = useState(() => savedNav?.locInput ?? "");
  const [applied, setApplied] = useState(() => {
    if (savedNav?.applied) return savedNav.applied;
    // Older saved views stored a geocoded place only.
    const p = savedNav?.place;
    return p ? { query: p.label, center: { lat: p.lat, lng: p.lng } } : null;
  });
  const [geoBusy, setGeoBusy] = useState(false);
  // Real boundary { place, b } of the searched place; kept out of `applied` so the
  // polygon is not saved into the Back-navigation state (re-fetched on restore).
  const [boundary, setBoundary] = useState(null);
  const geoReq = useRef(0);
  const firstCountryRun = useRef(true);
  // After a new search, move the selected day to the next date that has a match.
  const pendingJump = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    // Render progressively: events are fetched in pages of CALENDAR_PAGE_SIZE (200), so
    // a country loads in only a few steps and the first page shows immediately instead
    // of the whole list blocking behind the loading skeleton.
    const publish = (rows) => {
      if (!controller.signal.aborted) { setEvents(rows); setLoading(false); }
    };
    setEvents([]); setLoading(true);
    fetchCalendarEvents(country, publish, 0, [], controller.signal)
      .then(publish)
      .catch(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [country]);
  // The within-country location filter is scoped to a country, so clear it when
  // the global country changes — but NOT on the initial mount, so a restored
  // location (Back navigation) survives.
  useEffect(() => {
    if (firstCountryRun.current) {
      firstCountryRun.current = false;
      return;
    }
    geoReq.current += 1;
    setLocInput("");
    setApplied(null);
    setGeoBusy(false);
  }, [country]);

  // Persist the current calendar/search view for this history entry so pressing
  // Back rebuilds the same view (window scroll is restored by App.js).
  useEffect(() => {
    saveNavState(navKey, { year, month, selectedKey, locInput, applied });
  }, [navKey, year, month, selectedKey, locInput, applied]);

  const countryEvents = useMemo(
    () => events.filter((ev) => (ev.country_code || "GB").toUpperCase() === country),
    [events, country]
  );

  // Events narrowed to the selected country and, when a search is applied, to the
  // text/state matches plus (for city/town/postcode searches) anything within
  // AREA_RADIUS_KM of the geocoded place. Client-side over the already-loaded
  // set — no extra fetch, and it re-runs as later pages of the country stream in.
  const appliedQuery = applied ? applied.query : null;
  useEffect(() => {
    const place = appliedQuery ? parseQuery(appliedQuery).place : "";
    if (place.length < 3) {
      setBoundary(null);
      return undefined;
    }
    let cancelled = false;
    fetchBoundary(place, country).then((b) => {
      if (!cancelled) setBoundary({ place, b });
    });
    return () => {
      cancelled = true;
    };
  }, [appliedQuery, country]);

  const search = useMemo(() => {
    if (!applied) return null;
    const parsed = parseQuery(applied.query);
    const activeBoundary = boundary && boundary.place === parsed.place ? boundary.b : null;
    const { region, regionOnly, hits } = searchEventsGeo(countryEvents, applied.query, country, activeBoundary);
    // Label by the state name only when the query was just a state ("TX" -> Texas).
    const label = regionOnly ? region.name : applied.query;
    // Nearby events (by distance from the geocoded place) only when nothing
    // matches as text — e.g. a postcode or a village with no listed events.
    if (hits.length || !applied.center) return { label, regionOnly, list: hits.map((h) => h.ev) };
    const { lat, lng } = applied.center;
    const typeOk = typePredicate(parsed.types);
    const near = countryEvents.filter(
      (ev) =>
        typeof ev.latitude === "number" &&
        typeof ev.longitude === "number" &&
        distanceKm(lat, lng, ev.latitude, ev.longitude) <= AREA_RADIUS_KM &&
        (!typeOk || typeOk(ev))
    );
    return { label, regionOnly, list: near };
  }, [applied, countryEvents, country, boundary]);

  const scopedEvents = search ? search.list : countryEvents;

  const applyLocation = async () => {
    const q = locInput.trim();
    const reqId = ++geoReq.current;
    setGeoBusy(false);
    if (!q) {
      setApplied(null);
      return;
    }
    pendingJump.current = true;
    const { hits } = searchEventsGeo(countryEvents, q, country, null);
    setApplied({ query: q, center: null });
    // Text matches need no geocode; only when nothing matches is the query looked
    // up (within the selected country) to show events nearby.
    if (hits.length) return;
    setGeoBusy(true);
    const center = await geocodePlace(parseQuery(q).place || q, country);
    if (reqId !== geoReq.current) return; // superseded
    setGeoBusy(false);
    if (center) setApplied({ query: q, center });
  };

  const clearLocation = () => {
    geoReq.current += 1;
    setLocInput("");
    setApplied(null);
    setGeoBusy(false);
  };

  // ymd -> Map(eventId -> {event, occ}); each occurrence contributes to every day
  // it spans. Dedupe per day by event id so a multi-occurrence event shows once.
  const dayMap = useMemo(() => {
    const m = new Map();
    scopedEvents.forEach((ev) => {
      (ev.occurrenceList || []).forEach((occ) => {
        // Days in the EVENT'S timezone, not the UTC date (support #279162).
        const [startYmd, endYmd] = occurrenceDays(occ, ev.timezone, ev.country_code) || [];
        eachDay(startYmd, endYmd, (ymd) => {
          let day = m.get(ymd);
          if (!day) {
            day = new Map();
            m.set(ymd, day);
          }
          if (!day.has(ev.id)) day.set(ev.id, { event: ev, occ });
        });
      });
    });
    return m;
  }, [scopedEvents]);

  const grid = useMemo(() => {
    const first = new Date(Date.UTC(year, month, 1));
    const startWeekday = (first.getUTCDay() + 6) % 7; // Mon=0 .. Sun=6
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;
    const cells = [];
    for (let i = 0; i < totalCells; i += 1) {
      const d = new Date(Date.UTC(year, month, 1 - startWeekday + i));
      cells.push({
        date: d,
        key: keyOf(d),
        day: d.getUTCDate(),
        inMonth: d.getUTCMonth() === month,
      });
    }
    return cells;
  }, [year, month]);

  const dayList = useMemo(() => {
    const day = dayMap.get(selectedKey);
    if (!day) return [];
    return [...day.values()].sort((a, b) => {
      const ta = a.occ.start_at ? new Date(a.occ.start_at).getTime() : Infinity;
      const tb = b.occ.start_at ? new Date(b.occ.start_at).getTime() : Infinity;
      if (ta !== tb) return ta - tb;
      return (a.event.name || "").localeCompare(b.event.name || "");
    });
  }, [dayMap, selectedKey]);

  // With a search applied: the dates (today onwards) that have at least one match.
  const matchDates = useMemo(
    () => (search ? [...dayMap.keys()].filter((k) => k >= todayKey).sort() : []),
    [search, dayMap]
  );
  const nextMatchDate = matchDates.find((k) => k > selectedKey) || null;

  const jumpTo = (ymd) => {
    const d = new Date(ymd + "T00:00:00Z");
    setSelectedKey(ymd);
    setYear(d.getUTCFullYear());
    setMonth(d.getUTCMonth());
  };

  // After a new search, if the selected day has no match, move to the first
  // upcoming date that does (matches can fall on any date, not just today).
  useEffect(() => {
    if (!pendingJump.current || !search) return;
    if (selectedKey >= todayKey && dayMap.has(selectedKey)) {
      pendingJump.current = false;
      return;
    }
    if (matchDates.length) {
      pendingJump.current = false;
      jumpTo(matchDates[0]);
    } else if (!loading && !geoBusy) {
      pendingJump.current = false;
    }
  }, [search, dayMap, matchDates, selectedKey, loading, geoBusy]);

  const goMonth = (delta) => {
    const d = new Date(Date.UTC(year, month + delta, 1));
    setYear(d.getUTCFullYear());
    setMonth(d.getUTCMonth());
  };

  const goToday = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth());
    setSelectedKey(todayKey);
    setPickerOpen(false);
  };

  const selectCell = (cell) => {
    setSelectedKey(cell.key);
    if (!cell.inMonth) {
      setYear(cell.date.getUTCFullYear());
      setMonth(cell.date.getUTCMonth());
    }
  };

  const openPicker = () => {
    setPickerYear(year);
    setPickerOpen((v) => !v);
  };

  const { count, sentinel } = useListingBatch(dayList.length, JSON.stringify([country, selectedKey, applied?.query, applied?.center?.lat]));
  return (
    <div data-testid="calendar-page" className="min-h-[100dvh] pb-28">
      <div className="sticky top-0 z-30 border-b border-white/10 bg-[#050505] px-5 pb-4 pt-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-extrabold tracking-tight">
            Calendar
          </h1>
          <div className="flex items-center gap-2">
            <CountrySelector />
            <AppMenu />
          </div>
        </div>
        <p className="mt-1 text-sm text-white/45">
          {"What's on"}
          {search?.regionOnly
            ? ` in ${search.label}`
            : applied
            ? ` matching “${applied.query}”`
            : ` across ${countryName(country)}`}
          , by date
        </p>

        {/* Search within the selected country (not "Near Me") */}
        <div className="mt-2">
          <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-[#121212] px-4 py-2.5">
            <SearchIcon className="h-4 w-4 shrink-0 text-[#ff2a00]" />
            <input
              data-testid="calendar-location-input"
              value={locInput}
              onChange={(e) => setLocInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyLocation();
              }}
              placeholder={`State, city, event or keyword in ${countryName(country)}`}
              className="w-full bg-transparent text-base sm:text-sm text-white placeholder:text-white/40 focus:outline-none"
            />
            {geoBusy ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-white/50" />
            ) : locInput || applied ? (
              <button
                data-testid="calendar-location-clear"
                onClick={clearLocation}
                className="shrink-0 text-white/45 transition-colors hover:text-white"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
            <button
              data-testid="calendar-location-search"
              onClick={applyLocation}
              className="shrink-0 rounded-full bg-[#ff2a00] px-3 py-1.5 text-xs font-semibold text-white transition-transform duration-200 active:scale-95"
            >
              Search
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {applied ? (
              <span
                data-testid="calendar-scope-chip"
                className="inline-flex items-center gap-1 rounded-full border border-[#ff2a00]/40 bg-[#ff2a00]/10 px-3 py-1 text-xs font-semibold text-[#ff2a00]"
              >
                <MapPin className="h-3 w-3" />
                {search ? search.label : applied.query}
              </span>
            ) : (
              <span
                data-testid="calendar-scope-chip"
                className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-white/60"
              >
                National · {countryName(country)}
              </span>
            )}
            {search && !loading && !geoBusy && (
              search.list.length ? (
                <span data-testid="calendar-search-count" className="text-xs text-white/50">
                  {search.list.length} {search.list.length === 1 ? "event" : "events"} · dates marked below
                  {nextMatchDate && (
                    <>
                      {" · "}
                      <button
                        data-testid="calendar-next-match"
                        onClick={() => jumpTo(nextMatchDate)}
                        className="font-semibold text-[#ff2a00] underline-offset-2 hover:underline"
                      >
                        Next date
                      </button>
                    </>
                  )}
                </span>
              ) : (
                <span data-testid="calendar-location-error" className="text-xs text-white/50">
                  No events in {countryName(country)} match “{applied.query}”. Try a state, city, event name or keyword.
                </span>
              )
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-md px-5 pt-5 md:flex md:max-w-6xl md:items-start md:gap-8">
        {/* Wide: LEFT calendar stays stationary (sticky); RIGHT day-events scroll */}
        <div className="md:sticky md:top-6 md:w-1/2 md:self-start">
        {/* Month / year header */}
        <div className="flex items-center justify-between">
          <button
            data-testid="calendar-prev-month"
            onClick={() => goMonth(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-[#121212] transition-transform duration-200 active:scale-90"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-5 w-5 text-white" />
          </button>
          <button
            data-testid="calendar-month-label"
            onClick={openPicker}
            className="font-display rounded-xl px-3 py-1 text-lg font-extrabold tracking-tight text-white transition-colors active:text-[#ff2a00]"
          >
            {MONTHS[month]} {year}
          </button>
          <button
            data-testid="calendar-next-month"
            onClick={() => goMonth(1)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-[#121212] transition-transform duration-200 active:scale-90"
            aria-label="Next month"
          >
            <ChevronRight className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="mt-2 flex justify-center">
          <button
            data-testid="calendar-today-btn"
            onClick={goToday}
            className="rounded-full border border-[#ff2a00]/40 bg-[#ff2a00]/10 px-4 py-1 text-xs font-semibold text-[#ff2a00] transition-transform duration-200 active:scale-95"
          >
            Today
          </button>
        </div>

        {/* Month / year picker */}
        {pickerOpen && (
          <div
            data-testid="calendar-picker"
            className="mt-3 rounded-2xl border border-white/10 bg-[#121212] p-4"
          >
            <div className="flex items-center justify-between">
              <button
                onClick={() => setPickerYear((y) => y - 1)}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-[#1a1a1a] active:scale-90"
                aria-label="Previous year"
                data-testid="calendar-picker-prev-year"
              >
                <ChevronLeft className="h-4 w-4 text-white" />
              </button>
              <span className="font-display text-base font-bold text-white">{pickerYear}</span>
              <button
                onClick={() => setPickerYear((y) => y + 1)}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-[#1a1a1a] active:scale-90"
                aria-label="Next year"
                data-testid="calendar-picker-next-year"
              >
                <ChevronRight className="h-4 w-4 text-white" />
              </button>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {MONTHS.map((m, i) => {
                const active = i === month && pickerYear === year;
                return (
                  <button
                    key={m}
                    data-testid={`calendar-picker-month-${i}`}
                    onClick={() => {
                      setYear(pickerYear);
                      setMonth(i);
                      setPickerOpen(false);
                    }}
                    className={`rounded-xl px-2 py-2 text-xs font-semibold transition-colors ${
                      active
                        ? "bg-[#ff2a00] text-white"
                        : "bg-[#1a1a1a] text-white/70 active:bg-[#242424]"
                    }`}
                  >
                    {m.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Weekday header */}
        <div className="mt-5 grid grid-cols-7 gap-1">
          {WEEKDAYS.map((w) => (
            <div key={w} className="py-1 text-center text-[11px] font-semibold uppercase tracking-wide text-white/40">
              {w}
            </div>
          ))}
        </div>

        {/* Month grid */}
        <div data-testid="calendar-grid" className="mt-1 grid grid-cols-7 gap-1">
          {grid.map((cell) => {
            const hasEvents = dayMap.has(cell.key);
            const isSelected = cell.key === selectedKey;
            const isToday = cell.key === todayKey;
            return (
              <button
                key={cell.key}
                data-testid={`calendar-day-${cell.key}`}
                onClick={() => selectCell(cell)}
                className={`relative flex h-11 flex-col items-center justify-center rounded-xl text-sm font-semibold transition-colors ${
                  isSelected
                    ? "bg-[#ff2a00] text-white"
                    : cell.inMonth
                    ? isToday
                      ? "border border-[#ff2a00]/50 text-[#ff2a00]"
                      : "text-white/85 active:bg-white/5"
                    : "text-white/25"
                }`}
              >
                <span>{cell.day}</span>
                {hasEvents && !isSelected && (
                  <span
                    className="absolute bottom-1.5 h-1.5 w-1.5 rounded-full bg-[#ff2a00]"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        </div>

        {/* Selected day + events */}
        <div className="mt-7 md:mt-0 md:w-1/2">
          <h2
            data-testid="calendar-selected-label"
            className="font-display text-xl font-extrabold tracking-tight text-white"
          >
            {formatSelected(selectedKey)}
          </h2>

          {loading ? (
            <div className="mt-4 grid grid-cols-2 gap-4">
              {[0, 1].map((i) => (
                <div key={i} className="h-[240px] animate-pulse rounded-3xl bg-white/5" />
              ))}
            </div>
          ) : dayList.length === 0 ? (
            <div
              data-testid="calendar-empty"
              className="mt-6 flex flex-col items-center rounded-3xl border border-white/10 bg-[#121212] px-6 py-10 text-center"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-[#1a1a1a]">
                <CalendarDays className="h-6 w-6 text-white/40" />
              </div>
              <p className="mt-4 text-sm text-white/55">
                No events listed for this date yet.
              </p>
            </div>
          ) : (
            <>
            <div data-testid="calendar-day-events" className="mt-4 grid grid-cols-2 gap-4">
              {dayList.slice(0, count).reduce((acc, { event, occ }, i) => {
                acc.push(
                  <EventCard
                    key={event.id}
                    event={occurrenceVariant(event, occ)}
                    index={i}
                  />
                );
                if ((i + 1) % 6 === 0) acc.push(<AdSlot key={`ad-${i}`} page="calendar" />);
                return acc;
              }, [])}
            </div>
            {count < dayList.length && <div ref={sentinel} className="h-4" />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
